import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import {
  buildStillTreatingRepairPatchFromValidationError,
  mergeFormDataPatchWithRepairs,
} from '@/components/intakes/utils/formDataSanitizers';
import { useHeaderVisibility } from '@/contexts/HeaderVisibilityContext';
import { useIntakePage } from '@/hooks/useIntakePage';
import {
  useGetIntakeQuery,
  useUpdateIntakeMutation,
} from '@/services/api/intakeService';
import { selectUser } from '@/state/selectors/root-selectors';

import IntakeLayout from '../../pages/IntakeLayout';
import { IntakeForm } from '../index';
import { isScreeningSheetEnabled, ScreeningSheet } from '../screening';
import { ScreeningAnswers } from '../screening/types';
import { ScreeningV2Data, useScreeningV2Enabled } from '../screening/v2';
import { IntakeFormDataProvider } from '../screening/v2/IntakeFormDataContext';
import {
  buildV2FieldsFromIntake,
  mergeIntakeTreatmentsWithV2,
} from '../screening/v2/utils/syncV2DataCollection';

export const IntakeFormPage = () => {
  const { intake_id } = useParams<{ intake_id: string }>();
  const location = useLocation();
  const user = useSelector(selectUser);
  const { isHeaderVisible, setHeaderVisible } = useHeaderVisibility();

  // Determine if this is a new intake or editing an existing one
  const isNewIntake = location.pathname === '/intakes/new';
  const intakeId = isNewIntake ? null : intake_id;

  // Get intake data directly to check for in-progress screening
  const {
    data: intakeData,
    error: intakeLoadError,
    refetch,
  } = useGetIntakeQuery(intakeId || '', {
    skip: !intakeId || isNewIntake,
    // Force refetch when intake ID changes to prevent stale cache in production
    refetchOnMountOrArgChange: true,
  });

  // Mutation for saving V2 screening data
  const [updateIntake] = useUpdateIntakeMutation();
  const [isRepairingLoadError, setIsRepairingLoadError] = useState(false);
  const [loadRepairFailed, setLoadRepairFailed] = useState(false);
  const attemptedLoadRepairRef = useRef<string | null>(null);

  // Force refetch when intake ID changes to clear any stale cached data
  useEffect(() => {
    if (intakeId && !isNewIntake) {
      refetch();
    }
  }, [intakeId, isNewIntake, refetch]);

  useEffect(() => {
    attemptedLoadRepairRef.current = null;
    setLoadRepairFailed(false);
    setIsRepairingLoadError(false);
  }, [intakeId]);

  const loadRepairPatch = useMemo(() => {
    if (
      !intakeLoadError ||
      typeof intakeLoadError !== 'object' ||
      !('data' in intakeLoadError)
    ) {
      return null;
    }

    return buildStillTreatingRepairPatchFromValidationError(
      (intakeLoadError as { data?: unknown }).data,
    );
  }, [intakeLoadError]);

  useEffect(() => {
    if (!intakeId || isNewIntake || !loadRepairPatch) {
      return undefined;
    }

    if (attemptedLoadRepairRef.current === intakeId) {
      return undefined;
    }

    attemptedLoadRepairRef.current = intakeId;
    setIsRepairingLoadError(true);
    setLoadRepairFailed(false);

    let cancelled = false;

    (async () => {
      try {
        await updateIntake({
          id: intakeId,
          skipOptimistic: true,
          form_data: loadRepairPatch,
        }).unwrap();
        if (!cancelled) {
          await refetch();
        }
      } catch {
        if (!cancelled) {
          setLoadRepairFailed(true);
        }
      } finally {
        if (!cancelled) {
          setIsRepairingLoadError(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [intakeId, isNewIntake, loadRepairPatch, refetch, updateIntake]);

  // Get firmId and matterId for the sidebar
  const {
    intake: { firmId, matterId },
  } = useIntakePage(intakeId ?? null, isNewIntake);

  // Check if user has access to screening feature
  const hasScreeningAccess = isScreeningSheetEnabled(user?.email);

  // Determine if we should show screening at all
  // CRITICAL: Only show screening if we have validated data for the correct intake
  const shouldShowScreening =
    hasScreeningAccess &&
    intakeData &&
    intakeData.id === intakeId &&
    !isNewIntake &&
    intakeId;

  // Ref for the screening container
  const screeningSectionRef = useRef<HTMLDivElement>(null);

  // Check if V2 screening should be used
  const caseType = intakeData?.form_data?.incident_info?.type;
  const existingV1Data = intakeData?.form_data?.screening;

  // Transform snake_case backend data to camelCase frontend data
  // Also merge in values from the main intake form for two-way sync
  const existingV2Data = useMemo((): ScreeningV2Data | undefined => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = intakeData?.form_data?.screening_v2 as any;
    if (!raw || raw.version !== 'v2') return undefined;

    // Transform AI extracted fields from snake_case to camelCase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aiFields: Record<string, any> = {};
    if (raw.ai_extracted_fields) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.entries(raw.ai_extracted_fields).forEach(([key, field]: [string, any]) => {
        aiFields[key] = {
          value: field.value,
          confidence: field.confidence,
          sourceQuote: field.source_quote,
          status: field.status,
        };
      });
    }

    // Get existing V2 data collection answers
    const existingAnswers = raw.data_collection_answers ?? {};

    // Reverse sync: Get values from intake form that may have been edited there
    // This enables two-way sync between screening and intake form
    // The intake form (form_data) is the SOURCE OF TRUTH, just like header fields
    const intakeFormAnswers = buildV2FieldsFromIntake(intakeData?.form_data, caseType);

    // Merge: Start with V2 answers, then OVERWRITE with intake form values
    // This ensures edits in the intake form are always reflected in screening
    // (same behavior as header fields like phone/email)
    const mergedAnswers = { ...existingAnswers };
    Object.entries(intakeFormAnswers).forEach(([fieldId, answer]) => {
      if (answer.value !== undefined && answer.value !== '') {
        // Preserve the 'asked' status from V2 if it exists
        const existingAsked = existingAnswers[fieldId]?.asked ?? false;
        mergedAnswers[fieldId] = {
          ...answer,
          asked: existingAsked,
        };
      }
    });

    // Sync medical treatments from intake form to V2
    // Uses merge function that preserves V2 end_date (richer data than intake's still_treating bool)
    // SKIP if V2 already has an explicit answer for the treatment field (e.g., user deleted
    // all providers or marked "none") — otherwise stale intake treatments get merged back in.
    const intakeTreatments = intakeData?.form_data?.medical_wage_history?.treatments;
    if (intakeTreatments && intakeTreatments.length > 0) {
      const normalizedCaseType = caseType?.toLowerCase().replace(/\s+/g, '_');
      const treatmentFieldId =
        normalizedCaseType === 'premise_liability' || normalizedCaseType === 'premises_liability'
          ? 'pl_medical_treatment'
          : 'medical_treatment';

      const v2TreatmentAnswer = existingAnswers[treatmentFieldId];
      const hasExplicitV2Answer = v2TreatmentAnswer !== undefined;

      if (!hasExplicitV2Answer) {
        const existingV2Treatments = existingAnswers[treatmentFieldId]?.arrayValue as Record<string, string>[] | undefined;
        const mergedTreatments = mergeIntakeTreatmentsWithV2(intakeTreatments, existingV2Treatments);

        if (mergedTreatments.length > 0) {
          mergedAnswers[treatmentFieldId] = {
            ...existingAnswers[treatmentFieldId],
            arrayValue: mergedTreatments,
            asked: existingAnswers[treatmentFieldId]?.asked ?? false,
          };
        }
      }
    }

    // Default to collect screen (2) for Mold (data collection only, no P0 questions)
    const defaultScreen = caseType?.toLowerCase() === 'mold' ? 2 : 1;

    return {
      version: 'v2',
      currentScreen: raw.current_screen ?? defaultScreen,
      qualificationAnswers: raw.qualification_answers ?? {},
      qualificationContextAsked: raw.qualification_context_asked ?? {},
      qualificationNotes: raw.qualification_notes ?? {},
      qualificationOutcome: raw.qualification_outcome ?? 'pending',
      qualificationCompletedAt: raw.qualification_completed_at ?? null,
      retainerSentAt: raw.retainer_sent_at ?? null,
      rejectedAt: raw.rejected_at ?? null,
      escalatedAt: raw.escalated_at ?? null,
      dataCollectionAnswers: mergedAnswers,
      collectedFields: raw.collected_fields ?? [],
      collectionCompletedAt: raw.collection_completed_at ?? null,
      transcriptStatus: raw.transcript_status ?? 'waiting',
      matchedCallId: raw.matched_call_id ?? null,
      aiExtractedFields: aiFields,
      humanReviewedFields: raw.human_reviewed_fields ?? {},
      reviewCompletedAt: raw.review_completed_at ?? null,
      intakerId: raw.intaker_id ?? null,
      intakerName: raw.intaker_name ?? '',
      // Read firstContactOutcome from model field, not JSON (persisted via end-intake API)
      firstContactOutcome: (intakeData?.first_contact_outcome as ScreeningV2Data['firstContactOutcome']) ?? null,
      endIntakeClickedAt: raw.end_intake_clicked_at ?? null,
    };
  }, [intakeData?.form_data, intakeData?.first_contact_outcome, caseType]);

  const { isV2Enabled } = useScreeningV2Enabled({
    caseType,
    existingScreeningData: existingV1Data,
    existingScreeningV2Data: existingV2Data,
    intakeCreatedAt: intakeData?.created_at,
  });

  // Extract screening-specific data (answers, notes, text_fields, in_progress)
  // For shared fields (client name, case type, etc.), use personal_info/incident_info as source of truth
  const screeningData = useMemo(() => {
    // CRITICAL: Verify intake data matches current intake ID to prevent cross-contamination
    if (intakeData && intakeData.id !== intakeId) {
      // Stale cached data - return empty until correct data loads
      return {
        answers: {} as ScreeningAnswers,
        notes: '',
        textFields: {},
        inProgress: false,
      };
    }

    const screening = intakeData?.form_data?.screening;
    const incidentInfo = intakeData?.form_data?.incident_info;

    // Use incident_info.description as the source of truth for bi-directional sync
    // Both screening and normal intake form write to this field, so it's always up-to-date
    // Fall back to screening.notes or general_notes for older intakes that may not have description
    const notes =
      incidentInfo?.description ||
      screening?.notes ||
      intakeData?.form_data?.general_notes ||
      '';

    // Build text_fields with reverse sync from intake form
    // Start with saved screening text_fields
    const textFields = { ...(screening?.text_fields || {}) };

    // Reverse sync: If present_complaints exists in intake form but not in screening,
    // populate the injury details field
    if (
      incidentInfo?.present_complaints &&
      !textFields.injured?.['Injury Details']
    ) {
      textFields.injured = {
        ...textFields.injured,
        'Injury Details': incidentInfo.present_complaints,
      };
    }

    // Reverse sync: If defendant insurance exists in intake form but not in screening,
    // populate the 3P carrier name and policy number fields
    const defendants = intakeData?.form_data?.insurance?.defendants;
    if (defendants && defendants.length > 0) {
      const defendant = defendants[0];
      if (
        (defendant.insurance_company_name &&
          !textFields.defendant_insured?.['3P Carrier Name']) ||
        (defendant.policy_number &&
          !textFields.defendant_insured?.['3P Policy Number'])
      ) {
        textFields.defendant_insured = {
          ...textFields.defendant_insured,
          ...(defendant.insurance_company_name &&
            !textFields.defendant_insured?.['3P Carrier Name'] && {
              '3P Carrier Name': defendant.insurance_company_name,
            }),
          ...(defendant.policy_number &&
            !textFields.defendant_insured?.['3P Policy Number'] && {
              '3P Policy Number': defendant.policy_number,
            }),
        };
      }
    }

    // Reverse sync: If client's (1P) insurance exists in intake form but not in screening,
    // populate the 1P carrier name and policy number fields
    const pipInsurance = intakeData?.form_data?.insurance?.pip_insurance;
    if (pipInsurance) {
      if (
        (pipInsurance.company_name &&
          !textFields.defendant_insured?.['1P Carrier Name']) ||
        (pipInsurance.policy_number &&
          !textFields.defendant_insured?.['1P Policy Number'])
      ) {
        textFields.defendant_insured = {
          ...textFields.defendant_insured,
          ...(pipInsurance.company_name &&
            !textFields.defendant_insured?.['1P Carrier Name'] && {
              '1P Carrier Name': pipInsurance.company_name,
            }),
          ...(pipInsurance.policy_number &&
            !textFields.defendant_insured?.['1P Policy Number'] && {
              '1P Policy Number': pipInsurance.policy_number,
            }),
        };
      }
    }

    return {
      // Screening-specific data
      answers: (screening?.answers || {}) as ScreeningAnswers,
      notes,
      textFields,
      inProgress: screening?.in_progress ?? false,
    };
  }, [intakeId, intakeData]);

  // Hide header for fullscreen screening experience (useLayoutEffect prevents flash)
  useLayoutEffect(() => {
    if (isV2Enabled && shouldShowScreening) {
      setHeaderVisible(false);
    } else {
      setHeaderVisible(true);
    }
    return () => {
      setHeaderVisible(true);
    };
  }, [isV2Enabled, shouldShowScreening, setHeaderVisible]);

  // Handle V2 screening data changes - auto-save to backend
  const saveV2TimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Keep ref to latest V2 data for unmount flush
  const latestV2DataRef = useRef<ScreeningV2Data | null>(null);
  const lastSavedV2Ref = useRef<string>('');

  const handleV2DataChange = useCallback(
    (data: ScreeningV2Data) => {
      if (!intakeId) return;
      latestV2DataRef.current = data;

      // Debounce saves to avoid too many API calls
      if (saveV2TimeoutRef.current) {
        clearTimeout(saveV2TimeoutRef.current);
      }

      saveV2TimeoutRef.current = setTimeout(async () => {
        try {
          // Filter out undefined answers for backend compatibility
          const filteredAnswers: Record<string, string> = {};
          Object.entries(data.qualificationAnswers).forEach(([key, value]) => {
            if (value !== undefined) {
              filteredAnswers[key] = value;
            }
          });

          // Transform AI extracted fields from camelCase to snake_case
          const transformedAiFields: Record<
            string,
            {
              value: string;
              confidence: number;
              source_quote: string | null;
              status: string;
            }
          > = {};
          Object.entries(data.aiExtractedFields).forEach(([key, field]) => {
            transformedAiFields[key] = {
              value: field.value,
              confidence: field.confidence,
              source_quote: field.sourceQuote,
              status: field.status,
            };
          });

          // form_data field saves are handled by IntakeFormDataContext auto-save.
          // This handler only saves screening_v2 workflow data.
          await updateIntake({
            id: intakeId,
            skipOptimistic: true,
            form_data: mergeFormDataPatchWithRepairs(intakeData?.form_data, {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              screening_v2: {
                version: data.version,
                // Clamp to max 3 for backend compatibility (screen 4 = AI Extract maps to 3)
                current_screen: Math.min(data.currentScreen, 3) as 1 | 2 | 3,
                qualification_answers: filteredAnswers,
                qualification_context_asked: data.qualificationContextAsked,
                qualification_notes: data.qualificationNotes,
                qualification_outcome: data.qualificationOutcome,
                qualification_completed_at: data.qualificationCompletedAt,
                retainer_sent_at: data.retainerSentAt,
                rejected_at: data.rejectedAt,
                escalated_at: data.escalatedAt,
                data_collection_answers: data.dataCollectionAnswers,
                collected_fields: data.collectedFields,
                collection_completed_at: data.collectionCompletedAt,
                transcript_status: data.transcriptStatus,
                matched_call_id: data.matchedCallId,
                ai_extracted_fields: transformedAiFields,
                human_reviewed_fields: data.humanReviewedFields,
                review_completed_at: data.reviewCompletedAt,
                intaker_id: data.intakerId,
                intaker_name: data.intakerName,
                // Note: first_contact_outcome is saved via the end-intake API endpoint, not here
                end_intake_clicked_at: data.endIntakeClickedAt,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any,
            }),
          }).unwrap();
          lastSavedV2Ref.current = JSON.stringify(data);
        } catch {
          // Silent fail for auto-save
        }
      }, 1000);
    },
    [intakeId, intakeData?.form_data, updateIntake],
  );

  // Cleanup V2 save timeout on unmount — flush pending V2 data to prevent loss
  useEffect(() => {
    return () => {
      if (saveV2TimeoutRef.current) {
        clearTimeout(saveV2TimeoutRef.current);
      }
      // Fire pending V2 save if data changed since last save
      const data = latestV2DataRef.current;
      if (data && JSON.stringify(data) !== lastSavedV2Ref.current && intakeId) {
        // Build the same payload as the debounced save (fire and forget)
        const filteredAnswers: Record<string, string> = {};
        Object.entries(data.qualificationAnswers).forEach(([key, value]) => {
          if (value !== undefined) filteredAnswers[key] = value;
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aiFields: Record<string, any> = {};
        Object.entries(data.aiExtractedFields).forEach(([key, field]) => {
          aiFields[key] = { value: field.value, confidence: field.confidence, source_quote: field.sourceQuote, status: field.status };
        });
        updateIntake({
          id: intakeId,
          skipOptimistic: true,
          form_data: mergeFormDataPatchWithRepairs(intakeData?.form_data, {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            screening_v2: {
              version: data.version,
              current_screen: Math.min(data.currentScreen, 3) as 1 | 2 | 3,
              qualification_answers: filteredAnswers,
              qualification_context_asked: data.qualificationContextAsked,
              qualification_notes: data.qualificationNotes,
              qualification_outcome: data.qualificationOutcome,
              qualification_completed_at: data.qualificationCompletedAt,
              retainer_sent_at: data.retainerSentAt,
              rejected_at: data.rejectedAt,
              escalated_at: data.escalatedAt,
              data_collection_answers: data.dataCollectionAnswers,
              collected_fields: data.collectedFields,
              collection_completed_at: data.collectionCompletedAt,
              transcript_status: data.transcriptStatus,
              matched_call_id: data.matchedCallId,
              ai_extracted_fields: aiFields,
              human_reviewed_fields: data.humanReviewedFields,
              review_completed_at: data.reviewCompletedAt,
              intaker_id: data.intakerId,
              intaker_name: data.intakerName,
              end_intake_clicked_at: data.endIntakeClickedAt,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          }),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]);

  // Form data save is now handled by IntakeFormDataContext.
  // The context accumulates dirty changes and the save button flushes them.

  if (isRepairingLoadError || (loadRepairPatch && !loadRepairFailed)) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: isHeaderVisible ? 'calc(100vh - 56px)' : '100vh' }}
      >
        <div className="text-zinc-500">Repairing intake data...</div>
      </div>
    );
  }

  if (loadRepairPatch && loadRepairFailed) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: isHeaderVisible ? 'calc(100vh - 56px)' : '100vh' }}
      >
        <div className="max-w-md text-center text-zinc-500">
          We could not auto-repair this intake. Please refresh and try again,
          or repair the `still_treating` treatment field from the API.
        </div>
      </div>
    );
  }

  // CRITICAL: Validate that intake data matches current intake ID
  // This prevents showing stale cached data from a different intake
  const validatedIntakeData =
    intakeData && intakeData.id === intakeId ? intakeData : null;

  // If screening is not enabled or this is a new intake, show normal layout
  if (!shouldShowScreening) {
    return (
      <IntakeLayout
        firmId={firmId}
        intakeId={intakeId}
        matterId={matterId}
        onOpenScreening={undefined}
        hasScreeningData={false}
      >
        <div className="w-full">
          <IntakeForm intakeId={intakeId} isNewIntake={isNewIntake} />
        </div>
      </IntakeLayout>
    );
  }

  // Don't render ScreeningSheet until we have validated data for the correct intake
  if (!validatedIntakeData) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ height: isHeaderVisible ? 'calc(100vh - 56px)' : '100vh' }}
      >
        <div className="text-zinc-500">Loading intake data...</div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Unified Screening UI (full viewport, no scroll-snap dual-pane)
  // ---------------------------------------------------------------------------
  return (
    <IntakeFormDataProvider intakeId={intakeId ?? ''}>
      <div
        ref={screeningSectionRef}
        style={{ height: '100vh' }}
      >
        <ScreeningSheet
        key={intakeId}
        intakeId={intakeId ?? ''}
        // Use canonical sources (personal_info, incident_info) for shared data
        // CRITICAL: Use validatedIntakeData to ensure we never show wrong intake's data
        clientName={
          validatedIntakeData.form_data?.personal_info?.full_name || 'Unknown'
        }
        clientEmail={validatedIntakeData.form_data?.personal_info?.email}
        clientPhoneNumber={
          validatedIntakeData.form_data?.personal_info?.phone
        }
        firmId={validatedIntakeData.firm}
        firmName={validatedIntakeData.firm_name}
        onComplete={refetch}
        // Shared fields from canonical sources
        initialCaseType={
          validatedIntakeData.form_data?.incident_info?.type || ''
        }
        initialLossDate={
          validatedIntakeData.form_data?.incident_info?.date || ''
        }
        initialState={
          validatedIntakeData.form_data?.incident_info?.state || ''
        }
        // Screening-specific data (V1)
        initialAnswers={screeningData?.answers || {}}
        initialTextFields={screeningData?.textFields || {}}
        initialNotes={screeningData?.notes || ''}
        isEmbedded
        showFooter
        // V2 mode props
        useV2={isV2Enabled}
        initialV2Data={existingV2Data}
        onV2DataChange={handleV2DataChange}
      />
      </div>
    </IntakeFormDataProvider>
  );
};
